import type { ColorToken, ColorTokenInput } from "./token.color";
import { convertColorStringToRgb } from "./wcag.convert-color-to-rgb";
import type { ContrastRatioOptions } from "./wcag.verify-contrast-ratio";
import { verifyWCAGContrastRatio } from "./wcag.verify-contrast-ratio";

/**
 * utility to handle WCAG contrast ratio verification between a background
 * color and text color, automatically inverting between two colors based on
 * the contrast ratio
 *
 * gracefully handles edge cases to allow selecting the preferred color as
 * often as it makes sense
 */
export function invertColorByWCAGContrastRatio(
  backgroundColor: string,
  textColor: {
    preferredColor: ColorToken | string;
    invertedColor: ColorToken | string;
  },
  { laxRatio, ...options }: ContrastRatioOptions = {},
): ColorToken | ColorTokenInput | undefined {
  const preferredColor =
    typeof textColor.preferredColor === "function"
      ? `${textColor.preferredColor.r.defaultValue} ${textColor.preferredColor.g.defaultValue} ${textColor.preferredColor.b.defaultValue}`
      : textColor.preferredColor;

  const invertedColor =
    typeof textColor.invertedColor === "function"
      ? `${textColor.invertedColor.r.defaultValue} ${textColor.invertedColor.g.defaultValue} ${textColor.invertedColor.b.defaultValue}`
      : textColor.invertedColor;

  if (
    verifyWCAGContrastRatio(backgroundColor, preferredColor, {
      ...options,
      laxRatio,
    })
  ) {
    return typeof textColor.preferredColor === "function"
      ? textColor.preferredColor
      : convertColorStringToRgb(textColor.preferredColor)?.toString();
  }

  if (!verifyWCAGContrastRatio(backgroundColor, invertedColor, options)) {
    // if the inverted color doesn't match WCAG ratio either, then use the
    // default color knowing we fail WCAG ratio
    console.warn(
      "invertColorByWCAGContrastRatio: no text colors match requirements for bgColor '%s', using '%s' instead of '%s'",
      backgroundColor,
      preferredColor,
      invertedColor,
    );

    return typeof textColor.preferredColor === "function"
      ? textColor.preferredColor
      : convertColorStringToRgb(textColor.preferredColor)?.toString();
  }

  return typeof textColor.invertedColor === "function"
    ? textColor.invertedColor
    : convertColorStringToRgb(textColor.invertedColor)?.toString();
}
